
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { ExternalLink, Heart, Sparkles, Tag } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";
import type { FavoriteItem } from "@/components/pages/FavoritesPage";

type FavoriteCardDesignLabPageProps = {
  items: FavoriteItem[];
};

type LayoutPreset = {
  id: string;
  name: string;
  subtitle: string;
  format: string;
};

const LAYOUT_PRESETS: LayoutPreset[] = [
  {
    id: "editorial-list",
    name: "Editorial List",
    subtitle: "Magazine-like recommendation rows with contained product imagery.",
    format: "Full-width list rows",
  },
  {
    id: "feature-archive",
    name: "Feature + Archive",
    subtitle: "One primary pick up top, compact archive picks beside and below.",
    format: "Hero feature with archive stack",
  },
  {
    id: "shop-ledger",
    name: "Shop Ledger",
    subtitle: "A luxury buying-guide format with useful columns and disciplined spacing.",
    format: "Catalog / comparison ledger",
  },
];

function normalizeImageUrl(rawUrl?: string | null): string | null {
  if (!rawUrl) return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  const firstToken = trimmed.split(/\s+/)[0];
  try {
    return new URL(firstToken).toString();
  } catch {
    return firstToken;
  }
}

function BrandMark({
  label,
  textColor,
  iconColor = "#c9934c",
}: {
  label: string;
  textColor: string;
  iconColor?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <Sparkles size={12} color={iconColor} />
      <p
        className="text-[10px] uppercase tracking-[0.18em]"
        style={{ fontFamily: fonts.body, color: textColor, fontWeight: 700 }}
      >
        {label}
      </p>
    </div>
  );
}

function FavoriteThumb({
  item,
  fit = "contain",
  paddingClass = "p-5",
  background,
}: {
  item: FavoriteItem;
  fit?: "contain" | "cover";
  paddingClass?: string;
  background?: string;
}) {
  const [failed, setFailed] = useState(false);
  const imageUrl = normalizeImageUrl(item.imageUrl);

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{
        background:
          background ?? "linear-gradient(160deg, rgba(248,241,232,1) 0%, rgba(255,255,255,1) 100%)",
      }}
    >
      {!imageUrl || failed ? (
        <Tag size={24} style={{ color: colors.navy }} />
      ) : (
        <img
          src={imageUrl}
          alt={item.title}
          className={`absolute inset-0 h-full w-full ${fit === "contain" ? "object-contain" : "object-cover"} ${paddingClass}`}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

function FavoriteCTA({
  item,
  className,
  style,
}: {
  item: FavoriteItem;
  className: string;
  style: React.CSSProperties;
}) {
  const content = (
    <>
      {item.ctaLabel}
      {item.openInNewTab ? <ExternalLink size={12} /> : null}
    </>
  );

  if (item.openInNewTab) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noreferrer"
        className={className}
        style={style}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={item.href} className={className} style={style}>
      {content}
    </Link>
  );
}

function SectionHeader({
  preset,
  index,
}: {
  preset: LayoutPreset;
  index: number;
}) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <p
          className="mb-1 text-[10px] uppercase tracking-[0.22em]"
          style={{
            fontFamily: fonts.body,
            color: `${colors.navy}77`,
            fontWeight: 700,
          }}
        >
          Direction {index + 1}
        </p>
        <h2
          className="text-2xl sm:text-3xl"
          style={{ fontFamily: fonts.logo, color: colors.navy }}
        >
          {preset.name}
        </h2>
        <p
          className="mt-1 text-sm"
          style={{
            fontFamily: fonts.body,
            color: `${colors.navy}99`,
            letterSpacing: "0.03em",
          }}
        >
          {preset.subtitle}
        </p>
      </div>
      <div
        className="rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.18em]"
        style={{
          borderColor: `${colors.navy}18`,
          color: `${colors.navy}aa`,
          backgroundColor: "rgba(255,255,255,0.72)",
          fontFamily: fonts.body,
          fontWeight: 700,
        }}
      >
        {preset.format}
      </div>
    </div>
  );
}

function EditorialListRow({
  item,
}: {
  item: FavoriteItem;
}) {
  return (
    <article className="grid gap-4 rounded-[28px] border border-[#e6d7c3] bg-[#fffaf4] p-4 shadow-[0_10px_24px_rgba(8,45,61,0.06)] sm:grid-cols-[128px_1fr_auto] sm:items-center">
      <div className="relative h-32 overflow-hidden rounded-[22px] border border-[#e2d3be] bg-white">
        <FavoriteThumb item={item} fit="contain" paddingClass="p-4" />
      </div>
      <div className="flex flex-col gap-2">
        <BrandMark
          label={item.brand || item.categoryName}
          textColor="#8f6d47"
          iconColor="#c9934c"
        />
        <h3
          className="line-clamp-2 text-[1.08rem] leading-snug sm:text-[1.16rem]"
          style={{ fontFamily: fonts.logo, color: colors.navy }}
        >
          {item.title}
        </h3>
        <p
          className="line-clamp-2 text-sm leading-relaxed"
          style={{ fontFamily: fonts.body, color: `${colors.navy}b3` }}
        >
          {item.description}
        </p>
      </div>
      <div className="sm:justify-self-end">
        <FavoriteCTA
          item={item}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.12em]"
          style={{
            backgroundColor: "#f4e5d0",
            color: "#815529",
            border: "1px solid #dbc2a0",
            fontFamily: fonts.button,
            fontWeight: 700,
          }}
        />
      </div>
    </article>
  );
}

function FeatureHero({
  item,
}: {
  item: FavoriteItem;
}) {
  return (
    <article className="grid gap-5 rounded-[34px] border border-[#eadbc7] bg-[#fff9f2] p-5 shadow-[0_16px_34px_rgba(8,45,61,0.08)] xl:grid-cols-[1.05fr_0.95fr]">
      <div className="relative min-h-[340px] overflow-hidden rounded-[28px] border border-[#e6d7c1] bg-white">
        <FavoriteThumb item={item} fit="contain" paddingClass="p-8 sm:p-10" />
      </div>
      <div className="flex flex-col gap-4">
        <BrandMark
          label={item.brand || item.categoryName}
          textColor="#8f6d47"
          iconColor="#c9934c"
        />
        <h3
          className="line-clamp-3 text-3xl leading-[1.02] sm:text-4xl"
          style={{ fontFamily: fonts.logo, color: colors.navy }}
        >
          {item.title}
        </h3>
        <p
          className="line-clamp-5 text-sm leading-relaxed sm:text-[0.95rem]"
          style={{ fontFamily: fonts.body, color: `${colors.navy}b8` }}
        >
          {item.description}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-3">
          {item.discountCode ? (
            <div
              className="rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.14em]"
              style={{
                borderColor: "#e4d3bd",
                color: "#9a4050",
                backgroundColor: "#fff1f3",
                fontFamily: fonts.body,
                fontWeight: 700,
              }}
            >
              Code: {item.discountCode}
            </div>
          ) : null}
          <FavoriteCTA
            item={item}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.12em]"
            style={{
              backgroundColor: colors.navy,
              color: "white",
              fontFamily: fonts.button,
              fontWeight: 700,
            }}
          />
        </div>
      </div>
    </article>
  );
}

function ArchiveRow({
  item,
}: {
  item: FavoriteItem;
}) {
  return (
    <article className="grid gap-3 rounded-[24px] border border-[#e6d7c3] bg-white p-3 shadow-[0_8px_18px_rgba(8,45,61,0.05)] sm:grid-cols-[88px_1fr_auto] sm:items-center">
      <div className="relative h-24 overflow-hidden rounded-[18px] border border-[#eadfcf] bg-[#fbf6ef]">
        <FavoriteThumb item={item} fit="contain" paddingClass="p-3" />
      </div>
      <div className="flex flex-col gap-1.5">
        <BrandMark
          label={item.brand || item.categoryName}
          textColor="#8f6d47"
          iconColor="#c9934c"
        />
        <h3
          className="line-clamp-2 text-[1rem] leading-snug"
          style={{ fontFamily: fonts.body, color: colors.navy, fontWeight: 700 }}
        >
          {item.title}
        </h3>
      </div>
      <div className="sm:justify-self-end">
        <FavoriteCTA
          item={item}
          className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]"
          style={{
            backgroundColor: "#f4e5d0",
            color: "#815529",
            border: "1px solid #dbc2a0",
            fontFamily: fonts.button,
            fontWeight: 700,
          }}
        />
      </div>
    </article>
  );
}

function LedgerRow({
  item,
  index,
}: {
  item: FavoriteItem;
  index: number;
}) {
  return (
    <article className="grid gap-3 rounded-[24px] border border-[#dadada] bg-white p-3 shadow-[0_8px_18px_rgba(0,0,0,0.05)] lg:grid-cols-[56px_92px_1.15fr_1fr_auto] lg:items-center">
      <div className="hidden lg:flex items-center justify-center">
        <p
          className="text-[11px] uppercase tracking-[0.18em]"
          style={{ fontFamily: fonts.body, color: "#666666", fontWeight: 700 }}
        >
          {String(index + 1).padStart(2, "0")}
        </p>
      </div>
      <div className="relative h-24 overflow-hidden rounded-[18px] border border-[#dddddd] bg-[#f8f8f8]">
        <FavoriteThumb
          item={item}
          fit="contain"
          paddingClass="p-3"
          background="linear-gradient(180deg, rgba(249,249,249,1) 0%, rgba(241,241,241,1) 100%)"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <BrandMark
          label={item.brand || item.categoryName}
          textColor="#5a5a5a"
          iconColor="#7a7a7a"
        />
        <h3
          className="line-clamp-2 text-[1rem] leading-snug"
          style={{ fontFamily: fonts.body, color: "#161616", fontWeight: 700 }}
        >
          {item.title}
        </h3>
      </div>
      <div className="flex flex-col gap-1">
        <p
          className="text-[10px] uppercase tracking-[0.14em]"
          style={{ fontFamily: fonts.body, color: "#7a7a7a", fontWeight: 700 }}
        >
          Why it belongs
        </p>
        <p
          className="line-clamp-2 text-sm leading-relaxed"
          style={{ fontFamily: fonts.body, color: "#444444" }}
        >
          {item.description}
        </p>
      </div>
      <div className="flex items-center gap-3 lg:justify-self-end">
        {item.discountCode ? (
          <div
            className="rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]"
            style={{
              borderColor: "#e1e1e1",
              color: "#444444",
              backgroundColor: "#f7f7f7",
              fontFamily: fonts.body,
              fontWeight: 700,
            }}
          >
            {item.discountCode}
          </div>
        ) : null}
        <FavoriteCTA
          item={item}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.12em]"
          style={{
            backgroundColor: "#161616",
            color: "white",
            fontFamily: fonts.button,
            fontWeight: 700,
          }}
        />
      </div>
    </article>
  );
}

function EditorialListLayout({
  items,
}: {
  items: FavoriteItem[];
}) {
  return (
    <div className="grid gap-4">
      {items.map((item) => (
        <EditorialListRow key={item.id} item={item} />
      ))}
    </div>
  );
}

function FeatureArchiveLayout({
  items,
}: {
  items: FavoriteItem[];
}) {
  const lead = items[0];
  const archive = items.slice(1);

  if (!lead) return null;

  return (
    <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
      <FeatureHero item={lead} />
      <div className="grid gap-4">
        {archive.map((item) => (
          <ArchiveRow key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

function ShopLedgerLayout({
  items,
}: {
  items: FavoriteItem[];
}) {
  return (
    <div className="overflow-hidden rounded-[30px] border border-[#d8d8d8] bg-[#fcfcfc] p-4 shadow-[0_14px_28px_rgba(0,0,0,0.06)]">
      <div className="mb-3 hidden items-center gap-3 px-2 lg:grid lg:grid-cols-[56px_92px_1.15fr_1fr_auto]">
        {["No.", "Preview", "Item", "Why It Belongs", "Action"].map((label) => (
          <p
            key={label}
            className="text-[10px] uppercase tracking-[0.18em]"
            style={{ fontFamily: fonts.body, color: "#7a7a7a", fontWeight: 700 }}
          >
            {label}
          </p>
        ))}
      </div>
      <div className="grid gap-3">
        {items.map((item, index) => (
          <LedgerRow key={item.id} item={item} index={index} />
        ))}
      </div>
    </div>
  );
}

function LayoutShowcase({
  preset,
  items,
}: {
  preset: LayoutPreset;
  items: FavoriteItem[];
}) {
  switch (preset.id) {
    case "editorial-list":
      return <EditorialListLayout items={items} />;
    case "feature-archive":
      return <FeatureArchiveLayout items={items} />;
    case "shop-ledger":
      return <ShopLedgerLayout items={items} />;
    default:
      return null;
  }
}

export default function FavoriteCardDesignLabPage({
  items,
}: FavoriteCardDesignLabPageProps) {
  const theme = usePalette();
  const [activeLayoutId, setActiveLayoutId] = useState<string>(LAYOUT_PRESETS[0].id);
  const [showAllLayouts, setShowAllLayouts] = useState(true);

  const previewItems = useMemo(() => items.slice(0, 5), [items]);
  const activePreset =
    LAYOUT_PRESETS.find((preset) => preset.id === activeLayoutId) ?? LAYOUT_PRESETS[0];
  const visiblePresets = showAllLayouts ? LAYOUT_PRESETS : [activePreset];

  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>

      <section className="relative overflow-hidden px-6 pb-12 pt-28 text-center">
        <Blob className="left-1/4 top-10 h-80 w-80" color={theme.blob1} />
        <Blob className="bottom-0 right-1/4 h-64 w-64" color={theme.blob2} />

        <div className="relative z-10 mx-auto max-w-4xl">
          <div
            className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs uppercase tracking-widest"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              backgroundColor: `${theme.accent}12`,
              color: theme.accent,
            }}
          >
            <Heart size={14} />
            Favorites Layout Lab
          </div>

          <h1
            className="mb-4 text-5xl leading-tight sm:text-6xl"
            style={{ fontFamily: fonts.logo, color: colors.navy }}
          >
            Three Serious
            <span
              style={{
                fontFamily: fonts.heading,
                color: theme.warm,
                marginLeft: 8,
                fontSize: "1.12em",
              }}
            >
              Page Directions
            </span>
          </h1>

          <p
            className="mx-auto max-w-2xl leading-relaxed"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: `${colors.navy}aa`,
              letterSpacing: "0.03em",
            }}
          >
            This is no longer a card-skin gallery. These are three different ways to
            structure the favorites page around inconsistent external product images
            without making the experience feel cheap.
          </p>
        </div>
      </section>

      <section className="sticky top-[73px] z-30 px-6 pb-8 backdrop-blur-md">
        <div
          className="mx-auto max-w-6xl rounded-2xl border p-4"
          style={{
            borderColor: `${colors.tan}22`,
            backgroundColor: `${colors.cream}f0`,
          }}
        >
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {LAYOUT_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => {
                    setActiveLayoutId(preset.id);
                    setShowAllLayouts(false);
                  }}
                  className={`cursor-pointer rounded-full px-3 py-2 text-xs uppercase tracking-[0.08em] transition-all ${
                    activeLayoutId === preset.id && !showAllLayouts ? "text-white" : "bg-white"
                  }`}
                  style={{
                    fontFamily: fonts.body,
                    fontWeight: 700,
                    ...(activeLayoutId === preset.id && !showAllLayouts
                      ? { backgroundColor: theme.accent }
                      : { color: `${colors.navy}bb` }),
                  }}
                >
                  {preset.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAllLayouts(true)}
                className={`cursor-pointer rounded-full px-4 py-2 text-xs uppercase tracking-[0.08em] ${
                  showAllLayouts ? "text-white" : ""
                }`}
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  ...(showAllLayouts
                    ? { backgroundColor: "#606b4c" }
                    : { backgroundColor: "white", color: `${colors.navy}bb` }),
                }}
              >
                Show All 3
              </button>
              <button
                onClick={() => setShowAllLayouts(false)}
                className={`cursor-pointer rounded-full px-4 py-2 text-xs uppercase tracking-[0.08em] ${
                  !showAllLayouts ? "text-white" : ""
                }`}
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  ...(!showAllLayouts
                    ? { backgroundColor: theme.accent }
                    : { backgroundColor: "white", color: `${colors.navy}bb` }),
                }}
              >
                Single Direction
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="mx-auto grid max-w-6xl gap-10">
          {previewItems.length === 0 ? (
            <div className="py-20 text-center">
              <p
                className="text-lg"
                style={{ fontFamily: fonts.body, color: colors.navy, fontWeight: 700 }}
              >
                No published favorite links found.
              </p>
            </div>
          ) : (
            visiblePresets.map((preset, index) => (
              <section key={preset.id} className="grid gap-5">
                <SectionHeader preset={preset} index={index} />
                <LayoutShowcase preset={preset} items={previewItems} />
              </section>
            ))
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
